import { createDemoState } from './data'

const demoState = createDemoState()

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function ok(data = null) {
  return Promise.resolve({ code: '200', msg: 'success', data: clone(data) })
}

function fail(msg = '失败', code = '500') {
  return Promise.resolve({ code, msg, data: null })
}

function page(list, pageNum = 1, pageSize = 10) {
  const current = Number(pageNum) || 1
  const size = Number(pageSize) || 10
  const start = (current - 1) * size
  return {
    total: list.length,
    list: list.slice(start, start + size)
  }
}

function nextId(list) {
  return list.length ? Math.max(...list.map(item => Number(item.id) || 0)) + 1 : 1
}

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('xm-user') || '{}')
  } catch (e) {
    return {}
  }
}

function saveUser(user) {
  localStorage.setItem('xm-user', JSON.stringify(user))
}

function typeById(id) {
  return demoState.energyTypes.find(item => Number(item.id) === Number(id))
}

function entityById(id) {
  return demoState.energyEntities.find(item => Number(item.id) === Number(id))
}

function monthOf(date) {
  return Number(String(date).slice(5, 7))
}

function sum(list, selector) {
  return list.reduce((acc, item) => acc + Number(selector(item) || 0), 0)
}

function matchText(value, keyword) {
  if (keyword === null || keyword === undefined || keyword === '') return true
  return String(value || '').toLowerCase().includes(String(keyword).toLowerCase())
}

function filterDateRange(list, field, startDate, endDate) {
  return list.filter(item => {
    const value = item[field]
    if (!value) return false
    if (startDate && value < startDate) return false
    if (endDate && value > endDate) return false
    return true
  })
}

function productBenchmarks(type) {
  return demoState.benchmarkData.filter(item => item.benchmarkType === type)
}

function energyTypeStats(items) {
  const grouped = {}
  items.forEach(item => {
    const key = item.energyTypeId
    if (!grouped[key]) {
      grouped[key] = {
        energyTypeId: item.energyTypeId,
        energyTypeName: item.energyTypeName,
        unit: item.energyTypeUnit,
        totalConsumption: 0,
        totalCost: 0,
        totalStandardCoal: 0,
        totalCarbonEmission: 0
      }
    }
    grouped[key].totalConsumption += Number(item.consumptionValue || 0)
    grouped[key].totalCost += Number(item.totalCost || 0)
    grouped[key].totalStandardCoal += Number(item.standardCoalValue || 0)
    grouped[key].totalCarbonEmission += Number(item.carbonEmission || 0)
  })
  return Object.values(grouped).map(item => ({
    ...item,
    totalConsumption: Number(item.totalConsumption.toFixed(2)),
    totalCost: Number(item.totalCost.toFixed(2)),
    totalStandardCoal: Number(item.totalStandardCoal.toFixed(2)),
    totalCarbonEmission: Number(item.totalCarbonEmission.toFixed(2))
  }))
}

function energyTypeEntityStats(items) {
  const grouped = {}
  items.forEach(item => {
    const key = `${item.energyTypeId}-${item.entityId}`
    if (!grouped[key]) {
      grouped[key] = {
        energyTypeId: item.energyTypeId,
        energyTypeName: item.energyTypeName,
        entityId: item.entityId,
        entityName: item.entityName,
        totalConsumption: 0,
        totalCost: 0,
        totalStandardCoal: 0
      }
    }
    grouped[key].totalConsumption += Number(item.consumptionValue || 0)
    grouped[key].totalCost += Number(item.totalCost || 0)
    grouped[key].totalStandardCoal += Number(item.standardCoalValue || 0)
  })
  return Object.values(grouped)
}

function monthlyConsumption() {
  const map = {}
  const allowedIds = new Set(
    demoState.energyBudgets
      .filter(item => item.budgetType === 'energy')
      .map(item => Number(item.entityId))
      .filter(id => !Number.isNaN(id))
  )
  demoState.energyConsumptions.forEach(item => {
    if (!allowedIds.has(Number(item.entityId))) return
    const month = monthOf(item.consumptionDate)
    if (!map[month]) map[month] = { month, totalEnergy: 0, totalCarbon: 0 }
    map[month].totalEnergy += Number(item.standardCoalValue || 0)
    map[month].totalCarbon += Number(item.carbonEmission || 0)
  })
  return Array.from({ length: 12 }, (_, index) => {
    const month = index + 1
    const row = map[month] || { month, totalEnergy: 0, totalCarbon: 0 }
    return {
      month,
      totalEnergy: Number(row.totalEnergy.toFixed(2)),
      totalCarbon: Number(row.totalCarbon.toFixed(2))
    }
  })
}

function round(value, digits = 2) {
  const factor = 10 ** digits
  return Math.round(Number(value || 0) * factor) / factor
}

function entityCarbonSummary(items) {
  const grouped = {}
  items.forEach(item => {
    if (!grouped[item.entityId]) {
      grouped[item.entityId] = {
        entityId: item.entityId,
        entityName: item.entityName,
        totalCarbon: 0,
        totalEnergy: 0
      }
    }
    grouped[item.entityId].totalCarbon += Number(item.carbonEmission || 0)
    grouped[item.entityId].totalEnergy += Number(item.standardCoalValue || 0)
  })
  return Object.values(grouped)
}

function carbonBudgetByEntity() {
  const currentYear = new Date().getFullYear()
  return demoState.energyBudgets.reduce((acc, item) => {
    if (item.budgetType !== 'carbon') return acc
    if (!(Number(item.budgetYear) === currentYear || String(item.budgetYear) === String(currentYear))) return acc
    const entityId = Number(item.entityId)
    if (Number.isNaN(entityId)) return acc
    acc[entityId] = (acc[entityId] || 0) + Number(item.budgetValue || 0)
    return acc
  }, {})
}

function assetOverview() {
  const allocated = sum(demoState.carbonQuotaMonthly, item => item.allocated)
  const actual = sum(demoState.carbonQuotaMonthly, item => item.actualEmission)
  const ccer = sum(demoState.carbonQuotaMonthly, item => item.ccer)
  return {
    allocated: Number(allocated.toFixed(2)),
    used: Number(actual.toFixed(2)),
    remaining: Number((allocated - actual + ccer).toFixed(2)),
    ccer: Number(ccer.toFixed(2))
  }
}

function normalizeConsumption(record) {
  const type = typeById(record.energyTypeId)
  const entity = entityById(record.entityId)
  record.entityName = entity ? entity.entityName : record.entityName
  record.energyTypeName = type ? type.typeName : record.energyTypeName
  record.energyTypeUnit = type ? type.unit : record.energyTypeUnit
  record.consumptionValue = Number(record.consumptionValue || 0)
  record.unitPrice = Number(record.unitPrice || 0)
  record.totalCost = Number((record.consumptionValue * record.unitPrice).toFixed(2))
  record.standardCoalValue = Number((record.consumptionValue * Number((type && type.conversionFactor) || 0)).toFixed(4))
  record.carbonEmission = Number((record.consumptionValue * Number((type && type.carbonFactor) || 0)).toFixed(4))
  if (record.consumptionDate instanceof Date) {
    record.consumptionDate = record.consumptionDate.toISOString().slice(0, 10)
  }
}

function normalizeProduction(record) {
  const entity = entityById(record.entityId)
  record.entityName = entity ? entity.entityName : record.entityName
  if (record.productionDate instanceof Date) {
    record.productionDate = record.productionDate.toISOString().slice(0, 10)
  }
}

function normalizeBudget(record) {
  const entity = entityById(record.entityId)
  const type = typeById(record.energyTypeId)
  record.entityName = entity ? entity.entityName : record.entityName
  record.entityType = entity ? entity.entityType : record.entityType
  record.energyTypeName = type ? type.typeName : record.energyTypeName
}

function normalizeBenchmark(record) {
  const entity = entityById(record.entityId)
  record.entityName = entity ? entity.entityName : record.entityName
  if (record.assessmentDate instanceof Date) {
    record.assessmentDate = record.assessmentDate.toISOString().slice(0, 10)
  }
}

function parseConfig(input, extra = {}) {
  if (typeof input === 'string') {
    return { url: input, method: (extra.method || 'GET').toUpperCase(), ...extra }
  }
  return { ...(input || {}), method: ((input && input.method) || 'GET').toUpperCase() }
}

function matchResource(url, resource) {
  return url === `/${resource}` || url.startsWith(`/${resource}/`)
}

function crud(list, config, options = {}) {
  const { url, method, params = {}, data = {} } = config
  if (url.endsWith('/selectAll')) return ok(list)
  if (url.endsWith('/selectPage') || url.endsWith('/selectByPage') || url.endsWith('/selectPageForManager')) {
    let filtered = list.filter(item => {
      return Object.keys(params).every(key => {
        const value = params[key]
        if (['pageNum', 'pageSize', 'startDate', 'endDate'].includes(key)) return true
        if (value === null || value === undefined || value === '') return true
        if (['entityName', 'name', 'title', 'category', 'supplier', 'material', 'username', 'typeName'].includes(key)) {
          return matchText(item[key], value)
        }
        return String(item[key]) === String(value)
      })
    })
    if (params.startDate || params.endDate) {
      filtered = filterDateRange(filtered, options.dateField || 'consumptionDate', params.startDate, params.endDate)
    }
    return ok(page(filtered, params.pageNum, params.pageSize))
  }
  if (method === 'POST' && url.endsWith('/add')) {
    const record = clone(data)
    record.id = nextId(list)
    if (options.beforeSave) options.beforeSave(record)
    list.unshift(record)
    return ok()
  }
  if (method === 'PUT' && url.endsWith('/update')) {
    const index = list.findIndex(item => Number(item.id) === Number(data.id))
    if (index < 0) return fail('数据不存在')
    const next = { ...list[index], ...clone(data) }
    if (options.beforeSave) options.beforeSave(next)
    list.splice(index, 1, next)
    return ok()
  }
  if (method === 'DELETE' && /\/delete\/\d+$/.test(url)) {
    const id = Number(url.match(/\/delete\/(\d+)$/)[1])
    const index = list.findIndex(item => Number(item.id) === id)
    if (index >= 0) list.splice(index, 1)
    return ok()
  }
  if (method === 'DELETE' && url.endsWith('/delete/batch')) {
    const ids = (config.data || []).map(id => Number(id))
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (ids.includes(Number(list[i].id))) list.splice(i, 1)
    }
    return ok()
  }
  return null
}

function productLineIntensity(items, productions) {
  const productionMap = {}
  productions.forEach(item => {
    if (!productionMap[item.entityId]) {
      productionMap[item.entityId] = {
        entityId: item.entityId,
        productLine: item.entityName,
        production: 0,
        output: 0,
        added: 0
      }
    }
    productionMap[item.entityId].production += Number(item.productionValue || 0)
    productionMap[item.entityId].output += Number(item.outputValue || 0)
    productionMap[item.entityId].added += Number(item.industrialAddedValue || 0)
  })

  const energyMap = {}
  items.forEach(item => {
    if (!energyMap[item.entityId]) {
      energyMap[item.entityId] = {
        entityId: item.entityId,
        energyConsumption: 0
      }
    }
    energyMap[item.entityId].energyConsumption += Number(item.standardCoalValue || 0)
  })

  return Object.values(productionMap).map(item => {
    const energyConsumption = Number(((energyMap[item.entityId] && energyMap[item.entityId].energyConsumption) || 0).toFixed(2))
    const unitProductEnergy = item.production > 0 ? Number((energyConsumption * 1000 / item.production).toFixed(2)) : 0
    return {
      entityId: item.entityId,
      productLine: item.productLine,
      production: Number(item.production.toFixed(2)),
      energyConsumption,
      unitProductEnergy,
      nationalLimit: item.entityId === 6 ? 88 : item.entityId === 7 ? 92 : 135
    }
  })
}

function monthlyEnergyIntensity(startDate, endDate) {
  // 支持跨年查询：遍历所有12个月的数据
  const list = []
  const startYear = Number(String(startDate || '').slice(0, 4)) || new Date().getFullYear()
  const startMonth = Number(String(startDate || '').slice(5, 7)) || 1
  const endYear = Number(String(endDate || '').slice(0, 4)) || new Date().getFullYear()
  const endMonth = Number(String(endDate || '').slice(5, 7)) || 12

  let year = startYear
  let month = startMonth
  while (year < endYear || (year === endYear && month <= endMonth)) {
    const items = demoState.energyConsumptions.filter(item => {
      const itemMonth = monthOf(item.consumptionDate)
      const itemYear = Number(String(item.consumptionDate || '').slice(0, 4))
      return itemMonth === month && itemYear === year
    })
    const productions = demoState.productionData.filter(item => {
      const itemMonth = monthOf(item.productionDate)
      const itemYear = Number(String(item.productionDate || '').slice(0, 4))
      return itemMonth === month && itemYear === year
    })
    const totalEnergy = sum(items, item => item.standardCoalValue)
    const totalProduction = sum(productions, item => item.productionValue)
    const totalOutput = sum(productions, item => item.outputValue)
    const totalAdded = sum(productions, item => item.industrialAddedValue)
    list.push({
      month: `${year}-${String(month).padStart(2, '0')}`,
      unitProductEnergy: totalProduction > 0 ? Number((totalEnergy * 1000 / totalProduction).toFixed(2)) : 0,
      unitOutputEnergy: totalOutput > 0 ? Number((totalEnergy / totalOutput).toFixed(4)) : 0,
      unitAddedValueEnergy: totalAdded > 0 ? Number((totalEnergy / totalAdded).toFixed(4)) : 0
    })
    month += 1
    if (month > 12) { month = 1; year += 1 }
  }
  return list
}

function productionEntityIds(startDate, endDate) {
  return new Set(
    filterDateRange(demoState.productionData, 'productionDate', startDate, endDate)
      .map(item => Number(item.entityId))
  )
}

function energyFlowKeyPoints(startDate, endDate, targetUtilization, defaultK) {
  const scopedProduction = filterDateRange(demoState.productionData, 'productionDate', startDate, endDate)
    .filter(item => item.dataType === 'product_line')
  const scopedConsumption = filterDateRange(demoState.energyConsumptions, 'consumptionDate', startDate, endDate)
  const scopeIds = new Set(scopedProduction.map(item => Number(item.entityId)))

  const raw = Array.from(scopeIds).map(entityId => {
    const entity = entityById(entityId)
    const energyInput = sum(scopedConsumption.filter(item => Number(item.entityId) === entityId), item => item.standardCoalValue)
    const production = sum(scopedProduction.filter(item => Number(item.entityId) === entityId), item => item.productionValue)
    return {
      process: entity ? entity.entityName : `实体${entityId}`,
      energyInput,
      production
    }
  }).filter(item => item.energyInput > 0)

  const totalInput = sum(raw, item => item.energyInput)
  const totalProduction = sum(raw, item => item.production)
  const effectiveTargetUtilization = Number(targetUtilization) > 0 && Number(targetUtilization) <= 1 ? Number(targetUtilization) : 0.85
  const effectiveDefaultK = Number(defaultK) > 0 ? Number(defaultK) : 0.15
  const k = totalProduction > 0 && totalInput > 0 ? (totalInput * effectiveTargetUtilization) / totalProduction : effectiveDefaultK

  return raw.map(item => {
    const energyOutput = Math.min(item.production * k, item.energyInput)
    const energyLoss = Math.max(item.energyInput - energyOutput, 0)
    const lossRate = item.energyInput > 0 ? energyLoss * 100 / item.energyInput : 0
    return {
      priority: lossRate >= 15 ? '高' : lossRate >= 10 ? '中' : '低',
      process: item.process,
      energyInput: round(item.energyInput),
      energyOutput: round(energyOutput),
      energyLoss: round(energyLoss),
      lossRate: round(lossRate, 1),
      problem: '单位产量能耗偏高，存在节能空间'
    }
  }).sort((a, b) => b.energyInput - a.energyInput)
}

function energyFlowBalance(startDate, endDate, targetUtilization, defaultK) {
  const scopedConsumption = filterDateRange(demoState.energyConsumptions, 'consumptionDate', startDate, endDate)
  const scopeIds = productionEntityIds(startDate, endDate)
  const inputRows = demoState.energyTypes.map(type => {
    const items = scopedConsumption.filter(item => scopeIds.has(Number(item.entityId)) && Number(item.energyTypeId) === Number(type.id))
    const electricity = type.typeCode === 'electricity' ? sum(items, item => item.consumptionValue) : 0
    const gas = type.typeCode === 'natural_gas' ? sum(items, item => item.consumptionValue) : 0
    const diesel = type.typeCode === 'diesel' ? sum(items, item => item.consumptionValue) : 0
    const standardCoal = sum(items, item => item.standardCoalValue)
    return {
      category: '能源输入',
      item: type.typeName,
      electricity: round(electricity),
      gas: round(gas),
      diesel: round(diesel),
      standardCoal: round(standardCoal),
      percentage: 0
    }
  }).filter(item => item.standardCoal > 0).sort((a, b) => b.standardCoal - a.standardCoal)

  const totalInput = sum(inputRows, item => item.standardCoal)
  inputRows.forEach(row => {
    row.percentage = totalInput > 0 ? round(row.standardCoal * 100 / totalInput, 1) : 0
  })

  const keyPoints = energyFlowKeyPoints(startDate, endDate, targetUtilization, defaultK)
  const totalEffective = sum(keyPoints, item => item.energyOutput)
  const totalLoss = sum(keyPoints, item => item.energyLoss)

  return inputRows.concat([
    {
      category: '能源输出',
      item: '有效输出',
      electricity: 0,
      gas: 0,
      diesel: 0,
      standardCoal: round(totalEffective),
      percentage: totalInput > 0 ? round(totalEffective * 100 / totalInput, 1) : 0
    },
    {
      category: '能源输出',
      item: '能源损失',
      electricity: 0,
      gas: 0,
      diesel: 0,
      standardCoal: round(totalLoss),
      percentage: totalInput > 0 ? round(totalLoss * 100 / totalInput, 1) : 0
    }
  ])
}

function handle(config) {
  const { url, method, params = {}, data = {} } = config

  if (url === '/login' && method === 'POST') {
    const user = demoState.admins.find(item => item.username === data.username && item.password === data.password)
    if (!user) return fail('账号或密码错误')
    const result = {
      id: user.id,
      username: user.username,
      name: user.name,
      phone: user.phone,
      email: user.email,
      avatar: user.avatar,
      role: user.role,
      token: `demo-token-${user.role.toLowerCase()}`
    }
    saveUser(result)
    return ok(result)
  }

  if (url === '/register' && method === 'POST') {
    if (demoState.admins.some(item => item.username === data.username)) return fail('账号已存在')
    demoState.admins.push({
      id: nextId(demoState.admins),
      username: data.username,
      password: data.password,
      name: data.name || data.username,
      phone: data.phone || '',
      email: data.email || '',
      avatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
      role: 'USER'
    })
    return ok()
  }

  if (url === '/updatePassword' && method === 'PUT') {
    const me = currentUser()
    const target = demoState.admins.find(item => Number(item.id) === Number(me.id))
    if (!target) return fail('请重新登录', '401')
    target.password = data.newPassword || data.password || target.password
    return ok()
  }

  if (url === '/admin/update' && method === 'PUT') {
    const target = demoState.admins.find(item => Number(item.id) === Number(data.id))
    if (!target) return fail('用户不存在')
    Object.assign(target, data)
    const me = currentUser()
    if (Number(me.id) === Number(target.id)) saveUser({ ...me, ...target })
    return ok()
  }

  if (url === '/license/activate' && method === 'POST') return ok({ activated: true })
  if (url === '/excel/clear' && method === 'POST') return ok({ '清空表数量': 10, '已清空表': '演示数据已重置为默认状态' })
  if (url === '/notice/selectAll') return ok(demoState.notices)

  const adminCrud = matchResource(url, 'admin') ? crud(demoState.admins, config) : null
  if (adminCrud) return adminCrud
  const noticeCrud = matchResource(url, 'notice') ? crud(demoState.notices, config) : null
  if (noticeCrud) return noticeCrud
  const entityCrud = matchResource(url, 'energyEntity') ? crud(demoState.energyEntities, config) : null
  if (entityCrud) return entityCrud
  const typeCrud = matchResource(url, 'energyType') ? crud(demoState.energyTypes, config) : null
  if (typeCrud) return typeCrud
  const consumptionCrud = matchResource(url, 'energyConsumption') ? crud(demoState.energyConsumptions, config, { dateField: 'consumptionDate', beforeSave: normalizeConsumption }) : null
  if (consumptionCrud) return consumptionCrud
  const productionCrud = matchResource(url, 'productionData') ? crud(demoState.productionData, config, { dateField: 'productionDate', beforeSave: normalizeProduction }) : null
  if (productionCrud) return productionCrud
  const budgetCrud = matchResource(url, 'energyBudget') ? crud(demoState.energyBudgets, config, { beforeSave: normalizeBudget }) : null
  if (budgetCrud) return budgetCrud
  const benchmarkCrud = matchResource(url, 'benchmarkData') ? crud(demoState.benchmarkData, config, { beforeSave: normalizeBenchmark }) : null
  if (benchmarkCrud) return benchmarkCrud
  const verificationCrud = matchResource(url, 'carbonVerification') ? crud(demoState.carbonVerifications, config, { dateField: 'uploadTime' }) : null
  if (verificationCrud) return verificationCrud
  const transactionCrud = matchResource(url, 'carbonAsset/transaction') ? crud(demoState.carbonTransactions, config) : null
  if (transactionCrud) return transactionCrud
  const quotaCrud = matchResource(url, 'carbonAsset/quotaMonthly') ? crud(demoState.carbonQuotaMonthly, config) : null
  if (quotaCrud) return quotaCrud
  const supplyCrud = matchResource(url, 'supplyChainCarbon') ? crud(demoState.supplyChainCarbon, config) : null
  if (supplyCrud) return supplyCrud

  if (url.startsWith('/benchmarkData/selectByBenchmarkType/')) {
    return ok(productBenchmarks(url.split('/').pop()))
  }
  if (url === '/energyBudget/selectAll') return ok(demoState.energyBudgets)
  if (/^\/energyBudget\/getMonthlyEnergyConsumption\/\d+$/.test(url)) return ok(monthlyConsumption())
  if (url === '/energyConsumption/getCurrentYearData') return ok(demoState.energyConsumptions)

  if (url === '/energyConsumption/statisticsByEnergyType') {
    return ok(energyTypeStats(filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)))
  }
  if (url === '/energyConsumption/statisticsByEnergyTypeAndEntity') {
    return ok(energyTypeEntityStats(filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)))
  }
  if (url === '/energyConsumption/calculateEnergyIntensity' || url === '/energyConsumption/getEnterpriseEnergyIntensity') {
    const items = filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)
    const productions = filterDateRange(demoState.productionData, 'productionDate', params.startDate, params.endDate)
    const totalEnergy = sum(items, item => item.standardCoalValue)
    const totalProduction = sum(productions, item => item.productionValue)
    const totalOutput = sum(productions, item => item.outputValue)
    const totalIndustrialAddedValue = sum(productions, item => item.industrialAddedValue)
    if (url === '/energyConsumption/calculateEnergyIntensity') {
      return ok(productLineIntensity(items, productions))
    }
    return ok({
      totalEnergyConsumption: Number(totalEnergy.toFixed(2)),
      totalEnergy: Number(totalEnergy.toFixed(2)),
      totalProduction: Number(totalProduction.toFixed(2)),
      totalOutput: Number(totalOutput.toFixed(2)),
      totalIndustrialAddedValue: Number(totalIndustrialAddedValue.toFixed(2)),
      unitProductEnergy: totalProduction > 0 ? Number((totalEnergy * 1000 / totalProduction).toFixed(2)) : 0,
      unitOutputEnergy: totalOutput > 0 ? Number((totalEnergy / totalOutput).toFixed(4)) : 0,
      unitAddedValueEnergy: totalIndustrialAddedValue > 0 ? Number((totalEnergy / totalIndustrialAddedValue).toFixed(4)) : 0,
      nationalLimit: 135
    })
  }
  if (url === '/energyConsumption/getEnergyTypeStatistics') {
    return ok(energyTypeStats(filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)).map(item => ({
      energyTypeName: item.energyTypeName,
      totalConsumption: item.totalConsumption,
      totalCost: item.totalCost,
      totalStandardCoal: item.totalStandardCoal,
      unit: item.unit
    })))
  }
  if (url === '/energyConsumption/getEnergyIntensityTrend') {
    return ok(monthlyEnergyIntensity(params.startDate, params.endDate))
  }
  if (url === '/energyConsumption/getEnergyFlowBalance') {
    return ok(energyFlowBalance(params.startDate, params.endDate, params.targetUtilization, params.defaultK))
  }
  if (url === '/energyConsumption/getEnergyFlowKeyPoints') {
    return ok(energyFlowKeyPoints(params.startDate, params.endDate, params.targetUtilization, params.defaultK))
  }
  if (url === '/energyConsumption/getCarbonEmissionScopes') {
    const items = filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)
    const scope1 = sum(items.filter(item => item.energyTypeName !== '电力'), item => item.carbonEmission)
    const scope2 = sum(items.filter(item => item.energyTypeName === '电力'), item => item.carbonEmission)
    return ok({
      scope1: Number(scope1.toFixed(2)),
      scope2: Number(scope2.toFixed(2)),
      total: Number((scope1 + scope2).toFixed(2))
    })
  }
  if (url === '/energyConsumption/getCarbonFootprintRecords') {
    const items = filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate)
    const summary = entityCarbonSummary(items)
    const total = sum(summary, item => item.totalCarbon) || 1
    return ok(summary.map(item => ({
      entityName: item.entityName,
      unit: item.entityName,
      mainSource: item.entityName === '动力车间' ? '电力' : item.entityName === '制造二部' ? '柴油' : '天然气',
      energyInput: Number(item.totalEnergy.toFixed(2)),
      carbonEmission: Number(item.totalCarbon.toFixed(2)),
      percentage: Number((item.totalCarbon / total * 100).toFixed(1))
    })).sort((a, b) => b.carbonEmission - a.carbonEmission))
  }
  if (url === '/energyConsumption/getCarbonSourceDistribution') {
    const stats = energyTypeStats(filterDateRange(demoState.energyConsumptions, 'consumptionDate', params.startDate, params.endDate))
    return ok(stats.map(item => ({
      name: item.energyTypeName,
      value: Number(item.totalCarbonEmission.toFixed(2)),
      scope: item.energyTypeName === '电力' ? 'scope2' : 'scope1'
    })))
  }
  if (url === '/energyConsumption/getCarbonTrendAnalysis') {
    const xAxis = []
    const scope1Data = []
    const scope2Data = []
    const totalData = []
    for (let month = 1; month <= 12; month += 1) {
      xAxis.push(`${month}月`)
      const items = demoState.energyConsumptions.filter(item => monthOf(item.consumptionDate) === month)
      const scope1 = sum(items.filter(item => item.energyTypeName !== '电力'), item => item.carbonEmission)
      const scope2 = sum(items.filter(item => item.energyTypeName === '电力'), item => item.carbonEmission)
      scope1Data.push(Number(scope1.toFixed(2)))
      scope2Data.push(Number(scope2.toFixed(2)))
      totalData.push(Number((scope1 + scope2).toFixed(2)))
    }
    return ok({ xAxis, scope1Data, scope2Data, totalData })
  }
  if (url === '/energyConsumption/getCarbonWarningMonitor') {
    const now = new Date()
    const currentMonth = now.getMonth() + 1
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1
    const targetMonth = params.timePeriod === 'last_month' ? previousMonth : currentMonth
    const budgetMap = carbonBudgetByEntity()
    const allowedIds = new Set(Object.keys(budgetMap).map(Number))
    const items = demoState.energyConsumptions.filter(item =>
      monthOf(item.consumptionDate) === targetMonth && allowedIds.has(Number(item.entityId))
    )
    const summary = entityCarbonSummary(items)
    const rows = summary.map(item => {
      const annualQuota = Number(budgetMap[item.entityId] || 0)
      const quota = params.timePeriod === 'year'
        ? annualQuota
        : params.timePeriod === 'quarter'
          ? annualQuota / 4
          : annualQuota / 12
      const usage = quota > 0 ? item.totalCarbon / quota * 100 : 0
      return {
        entityName: item.entityName,
        monthQuota: Number(quota.toFixed(2)),
        currentMonthEmission: Number(item.totalCarbon.toFixed(2)),
        lastMonthEmission: Number((item.totalCarbon * 0.96).toFixed(2)),
        quotaUsage: `${usage.toFixed(1)}%`,
        warningLevel: usage >= 90 ? 'high' : usage >= 75 ? 'medium' : 'low'
      }
    }).sort((a, b) => parseFloat(b.quotaUsage) - parseFloat(a.quotaUsage))
    return ok(rows)
  }
  if (url === '/carbonAsset/overview') return ok(assetOverview())
  if (url === '/carbonAsset/complianceList') {
    return ok([{
      year: String(new Date().getFullYear()),
      allocated: sum(demoState.carbonQuotaMonthly, item => item.allocated),
      actualEmission: sum(demoState.carbonQuotaMonthly, item => item.actualEmission),
      ccer: sum(demoState.carbonQuotaMonthly, item => item.ccer),
      status: '进行中',
      remark: '演示年度汇总'
    }])
  }
  if (url === '/carbonAsset/transactionList') return ok(demoState.carbonTransactions)
  if (url === '/carbonAsset/quotaTrend') return ok({ monthlyData: demoState.carbonQuotaMonthly })
  if (url === '/carbonAsset/quotaMonthly/selectPage') return ok(page(demoState.carbonQuotaMonthly, params.pageNum, params.pageSize))
  if (url === '/carbonVerification/selectAll') return ok(demoState.carbonVerifications)
  if (url === '/supplyChainCarbon/selectByTimePeriod') {
    const countMap = {}
    const totalMap = {}
    demoState.supplyChainCarbon.forEach(item => {
      countMap[item.supplier] = (countMap[item.supplier] || 0) + 1
      totalMap[item.supplier] = (totalMap[item.supplier] || 0) + Number(item.emission || 0)
    })
    const visited = {}
    return ok(demoState.supplyChainCarbon.map(item => {
      const first = !visited[item.supplier]
      visited[item.supplier] = true
      return {
        ...item,
        totalEmission: first ? Number(totalMap[item.supplier].toFixed(2)) : null,
        rowspan: first ? countMap[item.supplier] : 0
      }
    }))
  }
  if (url === '/supplyChainCarbon/selectAll') return ok(demoState.supplyChainCarbon)
  if (url === '/supplyChainCarbon/getSupplierDetails') {
    return ok(demoState.supplyChainCarbon.filter(item => item.supplier === params.supplier))
  }

  return fail(`未实现的演示接口: ${method} ${url}`, '404')
}

function request(input) {
  return handle(parseConfig(input))
}

request.get = (url, config = {}) => handle(parseConfig(url, { ...config, method: 'GET' }))
request.post = (url, data, config = {}) => handle(parseConfig(url, { ...config, data, method: 'POST' }))
request.put = (url, data, config = {}) => handle(parseConfig(url, { ...config, data, method: 'PUT' }))
request.delete = (url, config = {}) => handle(parseConfig(url, { ...config, method: 'DELETE' }))

export default request
